import React, { useState, useEffect } from 'react';
import { useStore } from '@/store/useStore';
import api from '@/lib/api';
import { 
  TrendingUp,
  Target,
  Sparkles,
  Loader2,
  BarChart3,
  Calendar,
  RefreshCw
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Legend
} from 'recharts';
import { cn } from '@/lib/utils';

const formatNumber = (num) => {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num?.toFixed(0) || '0';
};

const Forecasting = () => {
  const { operators, priceDeck } = useStore();
  const [selectedOperator, setSelectedOperator] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (operators?.length > 0 && !selectedOperator) {
      setSelectedOperator(operators[0].id);
    }
  }, [operators, selectedOperator]);

  useEffect(() => {
    const fetchForecast = async () => {
      if (!selectedOperator) return;
      try {
        setLoading(true);
        const res = await api.get(`/forecast/operator/${selectedOperator}?price_deck=${priceDeck}`);
        setForecast(res.data);
      } catch (error) {
        console.error('Error fetching forecast:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchForecast();
  }, [selectedOperator, priceDeck]);

  const combinedChartData = forecast ? [
    ...forecast.historical.months.map((month, idx) => ({
      month,
      actual: forecast.historical.oil[idx],
      forecast: null,
    })),
    // Add overlap point
    {
      month: forecast.historical.months[forecast.historical.months.length - 1],
      actual: forecast.historical.oil[forecast.historical.oil.length - 1],
      forecast: forecast.historical.oil[forecast.historical.oil.length - 1],
    },
    ...forecast.forecast.months.slice(1).map((month, idx) => ({
      month,
      actual: null,
      forecast: forecast.forecast.oil[idx + 1],
    }))
  ] : [];

  const revenueChartData = forecast?.forecast.months.map((month, idx) => ({
    month,
    oil_revenue: forecast.forecast.oil_revenue[idx],
    gas_revenue: forecast.forecast.gas_revenue[idx],
    total: forecast.forecast.total_revenue[idx],
  })) || [];

  const totalForecastRevenue = forecast?.forecast.total_revenue.reduce((a, b) => a + b, 0) || 0;
  const totalForecastOil = forecast?.forecast.oil.reduce((a, b) => a + b, 0) || 0;

  return (
    <div className="space-y-3 sm:space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4">
        <div>
          <h1 className="text-lg sm:text-xl lg:text-2xl font-heading font-bold tracking-wide uppercase">
            AI Forecasting
          </h1>
          <p className="text-xs sm:text-sm text-muted-foreground">
            ML-powered production projections
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select 
            value={selectedOperator || ''} 
            onValueChange={setSelectedOperator}
          >
            <SelectTrigger 
              data-testid="forecast-operator-select"
              className="w-full sm:w-48 lg:w-64 bg-surface border-white/10 h-8 text-xs"
            >
              <SelectValue placeholder="Select operator" />
            </SelectTrigger>
            <SelectContent className="bg-surface border-white/10 max-h-80">
              {operators?.map(op => (
                <SelectItem key={op.id} value={op.id} className="text-xs">
                  {op.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button 
            variant="outline" 
            size="icon"
            onClick={() => setSelectedOperator(selectedOperator)}
            className="border-white/10 h-8 w-8"
          >
            <RefreshCw className={cn("w-3.5 h-3.5", loading && "animate-spin")} />
          </Button>
        </div>
      </div>

      {/* Forecast Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
        <Card className="bg-surface border-white/10">
          <CardContent className="p-2.5 sm:p-4">
            <div className="flex items-center gap-1.5 text-muted-foreground mb-1">
              <Target className="w-3.5 h-3.5 text-primary" />
              <span className="text-[10px] sm:text-xs uppercase tracking-wider">12-Mo Forecast</span>
            </div>
            <p className="text-lg sm:text-2xl font-heading font-bold">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : `${formatNumber(totalForecastOil)} BBL`}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-surface border-white/10">
          <CardContent className="p-2.5 sm:p-4">
            <div className="flex items-center gap-1.5 text-muted-foreground mb-1">
              <BarChart3 className="w-3.5 h-3.5 text-success" />
              <span className="text-[10px] sm:text-xs uppercase tracking-wider">Est. Revenue</span>
            </div>
            <p className="text-lg sm:text-2xl font-heading font-bold text-success">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : `$${formatNumber(totalForecastRevenue)}`}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-surface border-white/10">
          <CardContent className="p-2.5 sm:p-4">
            <div className="flex items-center gap-1.5 text-muted-foreground mb-1">
              <TrendingUp className="w-3.5 h-3.5 text-secondary" />
              <span className="text-[10px] sm:text-xs uppercase tracking-wider">Model</span>
            </div>
            <p className="text-sm sm:text-lg font-heading font-bold capitalize">
              {forecast?.decline_params?.oil?.model || '...'}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-surface border-white/10">
          <CardContent className="p-2.5 sm:p-4">
            <div className="flex items-center gap-1.5 text-muted-foreground mb-1">
              <Calendar className="w-3.5 h-3.5 text-primary" />
              <span className="text-[10px] sm:text-xs uppercase tracking-wider">Price</span>
            </div>
            <p className="text-lg sm:text-2xl font-heading font-bold">
              ${priceDeck}<span className="text-xs sm:text-sm text-muted-foreground">/bbl</span>
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Main Forecast Chart */}
      <Card className="bg-surface border-white/10">
        <CardHeader className="border-b border-white/5 p-2.5 sm:p-4 bg-white/[0.02]">
          <div className="flex items-center justify-between">
            <CardTitle className="text-xs sm:text-sm font-heading uppercase tracking-widest flex items-center gap-1.5">
              <Target className="w-3.5 h-3.5" />
              <span className="truncate">{forecast?.operator?.name || 'Loading...'}</span>
            </CardTitle>
            <Badge variant="outline" className="border-white/20 text-[10px] sm:text-xs font-mono">
              ARPS
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="p-2 sm:p-4">
          {loading ? (
            <div className="h-[250px] sm:h-[400px] flex items-center justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : (
            <div className="h-[250px] sm:h-[400px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={combinedChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                  <XAxis 
                    dataKey="month" 
                    stroke="#52525B" 
                    tick={{ fill: '#A1A1AA', fontSize: 10, fontFamily: 'IBM Plex Mono' }}
                    interval={1}
                  />
                  <YAxis 
                    stroke="#52525B" 
                    tick={{ fill: '#A1A1AA', fontSize: 10, fontFamily: 'IBM Plex Mono' }}
                    tickFormatter={formatNumber}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#0F0F10', 
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '4px',
                      fontFamily: 'IBM Plex Mono',
                      fontSize: '12px'
                    }}
                    formatter={(value, name) => [
                      formatNumber(value), 
                      name === 'actual' ? 'Actual (BBL)' : 'Forecast (BBL)'
                    ]}
                  />
                  <Legend 
                    wrapperStyle={{ fontFamily: 'IBM Plex Mono', fontSize: '12px' }}
                  />
                  <ReferenceLine 
                    x={forecast?.historical.months[forecast.historical.months.length - 1]} 
                    stroke="#52525B" 
                    strokeDasharray="3 3"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="actual" 
                    name="Actual"
                    stroke="#F59E0B" 
                    strokeWidth={2}
                    dot={false}
                    connectNulls={false}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="forecast" 
                    name="Forecast"
                    stroke="#10B981" 
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    dot={false}
                    connectNulls={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Revenue & AI Insight */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-2 sm:gap-4">
        {/* Revenue Projection */}
        <Card className="lg:col-span-2 bg-surface border-white/10">
          <CardHeader className="border-b border-white/5 p-2.5 sm:p-4 bg-white/[0.02]">
            <CardTitle className="text-xs sm:text-sm font-heading uppercase tracking-widest">
              Revenue @ ${priceDeck}/bbl
            </CardTitle>
          </CardHeader>
          <CardContent className="p-2 sm:p-4">
            {loading ? (
              <div className="h-[200px] sm:h-[300px] flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : (
              <div className="h-[200px] sm:h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={revenueChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                    <XAxis 
                      dataKey="month" 
                      stroke="#52525B" 
                      tick={{ fill: '#A1A1AA', fontSize: 10, fontFamily: 'IBM Plex Mono' }}
                    />
                    <YAxis 
                      stroke="#52525B" 
                      tick={{ fill: '#A1A1AA', fontSize: 10, fontFamily: 'IBM Plex Mono' }}
                      tickFormatter={(v) => `$${formatNumber(v)}`}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#0F0F10', 
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '4px',
                        fontFamily: 'IBM Plex Mono',
                        fontSize: '12px'
                      }}
                      formatter={(value, name) => [`$${formatNumber(value)}`, name]}
                    />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="oil_revenue" 
                      name="Oil Revenue"
                      stroke="#F59E0B" 
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="gas_revenue" 
                      name="Gas Revenue"
                      stroke="#3B82F6" 
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="total" 
                      name="Total"
                      stroke="#10B981" 
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        {/* AI Insight */}
        <Card className="bg-surface border-white/10 border-l-2 border-l-primary">
          <CardHeader className="border-b border-white/5 p-2.5 sm:p-4 bg-white/[0.02]">
            <CardTitle className="text-xs sm:text-sm font-heading uppercase tracking-widest flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5 text-primary" />
              AI Analysis
            </CardTitle>
          </CardHeader>
          <CardContent className="p-2.5 sm:p-4">
            {loading ? (
              <div className="flex items-center justify-center h-32 sm:h-48">
                <Loader2 className="w-5 h-5 animate-spin text-primary" />
              </div>
            ) : forecast?.ai_insight ? (
              <div className="space-y-3 sm:space-y-4">
                <p className="text-xs sm:text-sm text-foreground leading-relaxed">
                  {forecast.ai_insight}
                </p>
                <div className="pt-3 sm:pt-4 border-t border-white/5 space-y-1.5 sm:space-y-2">
                  <div className="flex justify-between text-[10px] sm:text-xs">
                    <span className="text-muted-foreground">Model</span>
                    <span className="font-mono text-foreground capitalize">
                      {forecast.decline_params?.oil?.model}
                    </span>
                  </div>
                  <div className="flex justify-between text-[10px] sm:text-xs">
                    <span className="text-muted-foreground">Initial Rate</span>
                    <span className="font-mono text-foreground">
                      {formatNumber(forecast.decline_params?.oil?.qi)} BBL/mo
                    </span>
                  </div>
                  <div className="flex justify-between text-[10px] sm:text-xs">
                    <span className="text-muted-foreground">Decline Rate</span>
                    <span className="font-mono text-foreground">
                      {(forecast.decline_params?.oil?.di * 100).toFixed(1)}%/mo
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-xs sm:text-sm text-muted-foreground">
                Select an operator to view insights
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Forecasting;
